import { decodeUrl as base64decodeUrl, encodeUrl as base64encodeUrl, } from 'compact-base64';
import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';
import throttle from 'lodash/throttle';
import getConfig from 'next/config';
import { Enum_LiveStream_Status, } from '@core/api';
import { getBrowser, getOS } from '@core/userAgent';
import { SHARE_LINK_PARAM } from '@embed/constants';
import { getPlatform, pruneNilValues } from '@embed/store/tools/utils';
import { toObject } from '@src/utils/qs';
import { AppContextType, Context } from './components/helpers';
import { ILiveStreamState } from './components/Player3/store/types';
export { logInfo, logWarning } from '@src/helpers';
const SANDBOX_FREE_DOMAINS = ['.fireworktv.com', 'fireworktv.com']; // Ignore sandbox and roll out on these domains
export const isWidgetLive = (params, publisherClient) => {
    if ((publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.status) === 'live') {
        return true;
    }
    let ignoreSandbox;
    try {
        if (params.page_url) {
            const pageUrl = new URL(params.page_url);
            ignoreSandbox =
                SANDBOX_FREE_DOMAINS.filter((item) => ((pageUrl === null || pageUrl === void 0 ? void 0 : pageUrl.hostname) || '').endsWith(item)).length > 0;
        }
    }
    catch (error) { }
    const query = toObject(location && location.search);
    if (ignoreSandbox || 'debug_fwn' in query) {
        return true;
    }
    return false;
};
export const createLinkToEmbedVideoPage = ({ appContext, params, video, }) => {
    const url = new URL(`${params.pwa_host || 'https://fw.tv'}/embed/videos/${video === null || video === void 0 ? void 0 : video.id}`);
    const { appContextType, username, publisherClientId, guestId, embedFeedConfig, trackingData, trackingDataPixel, } = appContext;
    const serializableAppContext = {
        params: Object.keys(params).reduce((map, key) => {
            if (isString(params[key]) || isBoolean(params[key])) {
                map[key] = params[key];
            }
            return map;
        }, Object.create(null)),
        appContextType,
        username,
        publisherClientId,
        guestId,
        embedFeedConfig: Object.assign(Object.assign({}, embedFeedConfig), { firstVideoId: video === null || video === void 0 ? void 0 : video.id }),
        trackingData,
        trackingDataPixel,
    };
    url.searchParams.append('app_context', JSON.stringify(serializableAppContext));
    return { newLocation: url.href, serializableAppContext };
};
export const abbreviateNumber = (number) => {
    const tier = (Math.log10(number) / 3) | 0;
    if (tier === 0) {
        return String(number);
    }
    const suffix = ['', 'k', 'M', 'G', 'T', 'P', 'E'][tier];
    const scale = Math.pow(10, tier * 3);
    const scaled = number / scale;
    const parts = String(scaled).match(/^([0-9]+)\.?([0-9]+)?$/);
    if (!parts) {
        return '';
    }
    return (`${parts[1]}.${parts[2] ? parts[2][0] : '0'}`.replace(/\.0$/, '') + suffix);
};
export const nowAndNowLocal = () => {
    const now = new Date();
    const nowLocal = new Date(+now - now.getTimezoneOffset() * 60 * 1000);
    return [now, nowLocal];
};
export const nowAndNowLocalToString = () => {
    const [now, nowLocal] = nowAndNowLocal();
    return [
        now.toISOString().replace('Z', ''),
        nowLocal.toISOString().replace('Z', ''),
    ];
};
export const uuidv4 = () => {
    try {
        // UUID v4 using crypto RNG
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore typing crypto hacks in TS is almost impossible
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (char) => {
            const random = window.crypto.getRandomValues(new Uint8Array(1))[0];
            const value = char ^ (random & (15 >> (char / 4)));
            return value.toString(16);
        });
    }
    catch (error) {
        // Fallback to simple version using Math.random() as RNG
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
            const random = (Math.random() * 16) | 0;
            const value = char === 'x' ? random : (random & 0x3) | 0x8;
            return value.toString(16);
        });
    }
};
export const removeEmojis = (text) => {
    // https://www.regextester.com/106421
    // the following code removes the following characters
    // 0x00A9(copy right symbol),
    // 0x00AE(register symbol),
    // 0x1F000 - 0x1F3FF, 0x1F400 - 0x1F7FF, 0x1F800 - 0x1FBFF (various emoji, symobols and pictographs)
    return typeof text === 'string'
        ? text
            .replace(/(\u00a9|\u00ae|[\u2000-\u2BFF]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g, '')
            .trim()
        : '';
};
export const detectWordpress = () => {
    const meta = typeof window !== 'undefined' &&
        document.querySelector("meta[name='generator']");
    return (meta &&
        meta.hasAttribute('content') &&
        meta.getAttribute('content').toLowerCase().startsWith('wordpress'));
};
//this is icky, but we can't feature test for safari bugs, so sometimes
//we just gotta browser sniff *shrug*
export const isSafari = typeof window !== 'undefined'
    ? /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)
    : false;
/*
  TODO: DIFFERENTIATING BETWEEN SUBS AND CAPTIONS
*/
export const setSubtitles = (videoEl, subtitles) => {
    videoEl
        .querySelectorAll('track[kind="captions"],track[kind="metadata"],track[kind="subtitles"]')
        .forEach((track) => track.remove());
    videoEl.crossOrigin = 'anonymous'; //so we can load vtts from the cdn
    const tracks = [];
    subtitles.forEach((sub) => {
        const track = document.createElement('track');
        track.src = sub.url;
        //we set kind to metadata so browsers won't try to show the captions themself
        track.kind = 'metadata';
        track.style.display = 'none';
        track.track.mode = 'hidden';
        track.srclang = sub.language;
        //for later when we support multiple langs
        //const isBrowserLang = navigator.language.indexOf(sub.language) === 0
        track.default = true;
        videoEl.appendChild(track);
        tracks.push(track);
    });
    return tracks;
};
export const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
// Handles dispatching tracking of in inviewport thumbnails
// for both grid and player sidebar
let isScrolling = null;
export const onGridScroll = (actions) => {
    const { processInViewportQueue, registerForAutoplay, trackScrollEndVideos } = actions;
    const processInViewportQueue_throttled = throttle(processInViewportQueue, 1000, {
        leading: false,
    });
    return () => {
        isScrolling && clearTimeout(isScrolling);
        isScrolling = setTimeout(() => {
            registerForAutoplay();
            trackScrollEndVideos();
        }, 500);
        processInViewportQueue_throttled();
    };
};
export const extractStartTimeFromQuerystring = (location) => {
    const { href } = location || {};
    const [, query] = href ? href.split('?') : [];
    const { start_time } = toObject(query);
    if (start_time) {
        return Number(start_time) || 0;
    }
    return 0;
};
export const extractPageUrlFromQuerystring = (location) => {
    const { href } = location || {};
    const [, query] = href ? href.split('?') : [];
    let { page_url } = toObject(query);
    if (page_url) {
        // if the href contains page_url, return its page_url
        // remove query if page_url still contains query
        if (String(page_url).includes('?')) {
            page_url = String(page_url).split('?')[0];
        }
        return page_url;
    }
    return null;
};
export const extractPageUrl = (state) => {
    // Note: extractPageUrl helper is being used by both FWN and PWA to determine
    // most probable page_url passed to tracking requests. Be aware for FWN it is
    // a placement url, however for PWA it is a referrer url.
    const { params } = state || {};
    const { referrer, in_iframe, page_url } = params || {};
    if (in_iframe && referrer && !page_url) {
        // value taken from referrer of an iframe
        // unless explicitelly passed page_url
        return referrer;
    }
    return page_url;
};
export const extractPageUrlPwa = (state) => {
    const { params } = state || {};
    const { location } = params || {};
    const { href } = location || {};
    return href || null;
};
export const appContextToTrackingData = (params) => {
    const { appContext } = params;
    const { appContextType } = appContext !== null && appContext !== void 0 ? appContext : {};
    switch (appContextType) {
        case AppContextType.WIDGET_CONTEXT_TYPE:
        case AppContextType.EMBED_CONTEXT_TYPE:
        case AppContextType.EMBED_STORYBLOCK_CONTEXT_TYPE:
        case AppContextType.EMBED_STORYLINK_CONTEXT_TYPE:
        case AppContextType.EMBED_CHANNEL_BUTTON_CONTEXT_TYPE: {
            if (appContext && 'trackingData' in appContext) {
                return appContext.trackingData;
            }
            return;
        }
        case AppContextType.DISCOVER_CONTEXT_TYPE: {
            return {
                app_context_type: appContextType,
                context: Context.CONTEXT_DISCOVER,
            };
        }
        case AppContextType.CHANNEL_CONTEXT_TYPE: {
            const { channelId, pageUrl } = appContext !== null && appContext !== void 0 ? appContext : {};
            return {
                app_context_type: appContextType,
                channel_id: channelId,
                context: Context.CONTEXT_CHANNEL,
                page_url: pageUrl,
            };
        }
        case AppContextType.HASHTAG_CONTEXT_TYPE: {
            const { hashtag, pageUrl } = appContext !== null && appContext !== void 0 ? appContext : {};
            return {
                app_context_type: appContextType,
                context: Context.CONTEXT_HASHTAG,
                hashtag: hashtag === null || hashtag === void 0 ? void 0 : hashtag.name,
                hashtag_id: hashtag === null || hashtag === void 0 ? void 0 : hashtag.id,
                page_url: pageUrl,
            };
        }
        case AppContextType.VIDEO_DETAIL_CONTEXT_TYPE: {
            return {
                app_context_type: appContextType,
                context: Context.CONTEXT_VIDEO_DETAIL,
            };
        }
    }
};
export const appContextToTrackingDataPixel = ({ appContext, }) => {
    switch (appContext === null || appContext === void 0 ? void 0 : appContext.appContextType) {
        case AppContextType.WIDGET_CONTEXT_TYPE:
        case AppContextType.EMBED_CONTEXT_TYPE:
        case AppContextType.EMBED_STORYBLOCK_CONTEXT_TYPE:
        case AppContextType.EMBED_STORYLINK_CONTEXT_TYPE:
        case AppContextType.EMBED_CHANNEL_BUTTON_CONTEXT_TYPE: {
            return appContext === null || appContext === void 0 ? void 0 : appContext['trackingDataPixel'];
        }
        case AppContextType.DISCOVER_CONTEXT_TYPE: {
            return {
                app_context_type: appContext.appContextType,
                context: Context.CONTEXT_DISCOVER,
            };
        }
        case AppContextType.CHANNEL_CONTEXT_TYPE: {
            const { channelId, pageUrl } = appContext;
            return {
                app_context_type: appContext.appContextType,
                _channel_id: channelId,
                context: Context.CONTEXT_CHANNEL,
                page_url: pageUrl,
            };
        }
        case AppContextType.HASHTAG_CONTEXT_TYPE: {
            const { hashtag, pageUrl } = appContext;
            return {
                app_context_type: appContext.appContextType,
                context: Context.CONTEXT_HASHTAG,
                hashtag: hashtag === null || hashtag === void 0 ? void 0 : hashtag.name,
                hashtag_id: hashtag === null || hashtag === void 0 ? void 0 : hashtag.id,
                page_url: pageUrl,
            };
        }
        case AppContextType.VIDEO_DETAIL_CONTEXT_TYPE: {
            return {
                app_context_type: appContext.appContextType,
                context: Context.CONTEXT_VIDEO_DETAIL,
            };
        }
    }
};
const commonMeta = () => {
    var _a, _b, _c, _d, _e, _f;
    return ({
        browser: getBrowser(),
        locale: typeof window !== 'undefined' && navigator.language,
        page_url: typeof window !== 'undefined' && window.location.href,
        platform: getPlatform(),
        product: ((_b = (_a = getConfig()) === null || _a === void 0 ? void 0 : _a.publicRuntimeConfig) === null || _b === void 0 ? void 0 : _b.product) || process.env.PRODUCT,
        product_version: ((_d = (_c = getConfig()) === null || _c === void 0 ? void 0 : _c.publicRuntimeConfig) === null || _d === void 0 ? void 0 : _d.productVersion) ||
            process.env.PRODUCT_VERSION,
        track_version: ((_f = (_e = getConfig()) === null || _e === void 0 ? void 0 : _e.publicRuntimeConfig) === null || _f === void 0 ? void 0 : _f.trackVersion) || process.env.TRACK_VERSION,
    });
};
export const getClientMeta = () => (Object.assign(Object.assign({}, commonMeta()), { country: typeof window !== 'undefined' &&
        navigator.language &&
        navigator.language.split('-')[1], os: getOS() }));
export const getPixelClientMeta = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const [now] = nowAndNowLocalToString();
    return Object.assign(Object.assign({}, commonMeta()), { event_properties: {
            page_load_id: typeof window !== 'undefined' && ((_b = (_a = window._fwn) === null || _a === void 0 ? void 0 : _a.analytics) === null || _b === void 0 ? void 0 : _b.page_load_id),
            _referrer_video_id: typeof window !== 'undefined' &&
                ((_d = (_c = window._fwn) === null || _c === void 0 ? void 0 : _c.analytics) === null || _d === void 0 ? void 0 : _d.fwreferrer_video_id),
            _referrer_channel_id: typeof window !== 'undefined' &&
                ((_f = (_e = window._fwn) === null || _e === void 0 ? void 0 : _e.analytics) === null || _f === void 0 ? void 0 : _f.fwreferrer_channel_id),
            referrer_origin: typeof window !== 'undefined' &&
                ((_h = (_g = window._fwn) === null || _g === void 0 ? void 0 : _g.analytics) === null || _h === void 0 ? void 0 : _h.fwreferrer_origin),
            referrer_guest_id: typeof window !== 'undefined' &&
                ((_k = (_j = window._fwn) === null || _j === void 0 ? void 0 : _j.analytics) === null || _k === void 0 ? void 0 : _k.fwreferrer_guest_id),
        }, client_event_time: now, client_upload_time: now, os_name: getOS() });
};
export const getPageUrl = (params, inIframe) => {
    try {
        if (inIframe && params.page_url) {
            return params.page_url;
        }
        return window.parent.location.href;
    }
    catch (e) {
        return;
    }
};
export const isTruthy = (value) => {
    if (!value || value === 'false' || value === '0') {
        return false;
    }
    return true;
};
export const isFalsy = (value) => {
    if ((value && value === 'false') ||
        (value && value === '0') ||
        value === false) {
        return true;
    }
    return false;
};
export const isValidEmail = (email) => {
    if (!email) {
        return false;
    }
    return !!email.match(/(.+)@(.+)\.(.+)/);
};
export const getFWPlayerParams = (appContext, video) => {
    var _a, _b, _c, _d, _e, _f;
    return Object.assign(Object.assign({}, pruneNilValues({
        v: 1,
        app_id: (_a = appContext.params) === null || _a === void 0 ? void 0 : _a.app_id,
        channel: (_b = appContext.params) === null || _b === void 0 ? void 0 : _b.channel,
        channel_id: (_c = appContext.params) === null || _c === void 0 ? void 0 : _c.channel_id,
        hashtag: (_d = appContext.params) === null || _d === void 0 ? void 0 : _d.hashtag,
        playlist_id: (_e = appContext.params) === null || _e === void 0 ? void 0 : _e.playlist,
    })), { video_id: (_f = video === null || video === void 0 ? void 0 : video.encoded_id) !== null && _f !== void 0 ? _f : '' });
};
export const composeFWPlayerParams = (appContext, video) => {
    // const feedTypes = {
    //   [AppContextType.DISCOVER_CONTEXT_TYPE]: 'discover',
    //   [AppContextType.VIDEO_DETAIL_CONTEXT_TYPE]: 'single_video',
    //   [AppContextType.CHANNEL_CONTEXT_TYPE]: 'channel_timeline',
    //   [AppContextType.CHANNEL_CONTEXT_TYPE]: 'channel_contextual',
    //   [AppContextType.CHANNEL_CONTEXT_TYPE]: 'channel_personalized',
    //   [AppContextType.EMBED_CONTEXT_TYPE]: 'playlist',
    //   [AppContextType.HASHTAG_CONTEXT_TYPE]: 'hashtag_popular'
    // }
    const data = getFWPlayerParams(appContext, video);
    try {
        return base64encodeUrl(JSON.stringify(data));
    }
    catch (err) {
        console.error(err);
        return '';
    }
};
export const parseFWPlayerParam = (w) => {
    var _a, _b;
    try {
        const base64data = ((_b = (_a = w.location.hash.match(new RegExp(`${SHARE_LINK_PARAM}=(?<base64data>[a-zA-Z0-9_]+)`))) === null || _a === void 0 ? void 0 : _a.groups) === null || _b === void 0 ? void 0 : _b.base64data) || toObject(w.location.search)[SHARE_LINK_PARAM];
        if (base64data) {
            const data = JSON.parse(base64decodeUrl(base64data));
            return data.video_id ? data : undefined;
        }
    }
    catch (err) {
        console.error('Router error', '-', err);
    }
    return undefined;
};
export const decorateWithFWReferrer = ({ url, video, appContext, params, }) => {
    var _a, _b;
    try {
        const baseUrl = new URL(url);
        if (params === null || params === void 0 ? void 0 : params.page_url) {
            baseUrl.searchParams.set('fwreferrer-origin', params.page_url);
        }
        if (((params === null || params === void 0 ? void 0 : params.channel) || (params === null || params === void 0 ? void 0 : params.channel_id)) &&
            ((_a = appContext === null || appContext === void 0 ? void 0 : appContext.channel) === null || _a === void 0 ? void 0 : _a.encoded_id)) {
            baseUrl.searchParams.set('fwreferrer-channel-id', (_b = appContext === null || appContext === void 0 ? void 0 : appContext.channel) === null || _b === void 0 ? void 0 : _b.encoded_id);
        }
        if (video === null || video === void 0 ? void 0 : video.encoded_id) {
            baseUrl.searchParams.set('fwreferrer-video-id', video.encoded_id);
        }
        if (appContext === null || appContext === void 0 ? void 0 : appContext.guestId) {
            baseUrl.searchParams.set('fwreferrer-guest-id', appContext === null || appContext === void 0 ? void 0 : appContext.guestId);
        }
        return baseUrl.href;
    }
    catch (err) {
        return url;
    }
};
export const getFWReferrerFromURL = (url) => {
    try {
        const parsed = new URL(url);
        return {
            video_id: parsed.searchParams.get('fwreferrer-video-id'),
            channel_id: parsed.searchParams.get('fwreferrer-channel-id'),
            origin: parsed.searchParams.get('fwreferrer-origin'),
            guest_id: parsed.searchParams.get('fwreferrer-guest-id'),
        };
    }
    catch (e) {
        return null;
    }
};
// TODO: DEPRECATE
export const videoIsLive = (video) => {
    if (!(video === null || video === void 0 ? void 0 : video.live_stream_status)) {
        return false;
    }
    return (video.live_stream_status === Enum_LiveStream_Status.ACTIVE ||
        video.live_stream_status === Enum_LiveStream_Status.PAUSED ||
        video.live_stream_status === Enum_LiveStream_Status.REPLAY);
};
// TODO: DEPRECATE
export const shouldSendLiveStreamStartWatch = (state) => state === ILiveStreamState.ACTIVE_PLAYING ||
    state === ILiveStreamState.ACTIVE_PAUSED ||
    state === ILiveStreamState.REPLAY_PLAYING;
export const stringHasContent = (str) => str.search(/[^ ]+/) !== -1;
export const updateVideoLiveStreamParams = (video, liveStream) => {
    const keyHasChanged = (key) => (video === null || video === void 0 ? void 0 : video[`live_stream_${key}`]) !== (liveStream === null || liveStream === void 0 ? void 0 : liveStream[key]);
    const keysToCheck = [
        'chat_enabled',
        'chat_in_replay_enabled',
        'ended_at',
        'playback_url',
        // 'provider',
        'replay_enabled',
        'replay_url',
        'scheduled_at',
        'started_at',
        'status',
        'viewers_count_enabled',
    ];
    if (video &&
        liveStream &&
        video.id === liveStream.video_id &&
        keysToCheck.some((key) => keyHasChanged(key))) {
        return Object.fromEntries(keysToCheck
            .filter((key) => keyHasChanged(key))
            .map((key) => [`live_stream_${key}`, liveStream[key]]));
    }
    return null;
};
